import { Row, Col, message, Table, Pagination } from "antd";
import { ColumnsType } from "antd/lib/table";
import { AppPageHeader } from "components/AppPageHeader";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { useDocumentTitle } from "hooks/usePageTitle";
import { useCallback, useEffect, useState } from "react";
import { GrView } from "react-icons/gr";
import styled from "styled-components";
import { NavLink, useSearchParams } from "react-router-dom";
import { IPo } from "common/interface";
import { getAllPOs } from "store/po/poSlice";
import { getDateAndTimeFromISODate, getDateFromISODate } from "common/utils";
import { AppActionStrip } from "components/AppActionStrip/AppActionStrip";
import { AppFilterButton, AppFilter, AppPoFilter } from "components/AppFilter";

type PartialPOType = Partial<IPo> & { key: string };
const MESSAGE_KEY = "all_pos_grn";

const headerKeys: { [key: string]: string } = {
  // challanId: "Challan ID",
  id: "PO ID",
  vendorId: "Vendor",
  // extPoIds: "Ext PO ID",
  poCategory: "Category",
  orderDate: "Order Date",
  // expectedDeliveryDate: "Expected Delivery",
  receivedDate: "Recieved",
  status: "Status",
  actions: "Actions",
};

const StyledPagination = styled(Pagination)`
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;
`;

const StyledActionLink = styled(NavLink)`
  border: 1px solid;
  padding: 0.2rem;
  border-radius: 50%;
  width: 1.5rem;
  height: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const GrnPage = () => {
  useDocumentTitle("All Purchase Orders GRN");
  const dispatch = useAppDispatch();
  const [columns, setColumns] = useState<ColumnsType<PartialPOType>>();
  const [rows, setRows] = useState<PartialPOType[]>();
  const { posList: pos, listStatus: status, listError: error } = useAppSelector((state) => state.po);
  const [filterState, setFilterState] = useState(false);
  const [searchParams] = useSearchParams();
  const [filterCount, setFilterCount] = useState(0);

  const getPOData = useCallback(
    (pageNum = 1, pageSize = 25, filter = "") => {
      dispatch(getAllPOs({ pageNo: pageNum, pageSize, filter }));
    },
    [dispatch]
  );

  useEffect(() => {
    const filter = searchParams.toString();
    setFilterCount(
      Object.keys(Object.fromEntries(new URLSearchParams(filter))).length
    );
    getPOData(1, 25, filter);
  }, [dispatch, getPOData, searchParams]);

  useEffect(() => {
    if (status === "loading") {
      message.loading({ content: "Loading Purchase Orders", key: MESSAGE_KEY });
    } else if (status === "success") {
      message.success({
        content: "Purchase Orders loaded!",
        key: MESSAGE_KEY,
      });
    } else if (status === "error") {
      message.error({ content: error, key: MESSAGE_KEY });
    }

    return () => {
      message.destroy();
    };
  }, [status, error]);

  useEffect(() => {
    const poData = pos && pos.data ? pos.data : [];
    const data: PartialPOType[] = poData.map((po, i) => {
      const {
        id,
        poId,
        vendorId,
        vendorName,
        vendorCode,
        orderDate,
        poCategory,
        receivedDate,
        status,
      } = po;

      const newRow = {
        id,
        poId,
        vendorId,
        vendorName,
        vendorCode,
        orderDate,
        poCategory,
        receivedDate,
        status,
      };
      newRow.receivedDate = getDateAndTimeFromISODate(newRow.receivedDate);
      // newRow.expectedDeliveryDate = getDateAndTimeFromISODate(newRow.expectedDeliveryDate);
      return { ...newRow, key: `po-${i}`, ellipses: true };
    });

    const tableHeaders: ColumnsType<PartialPOType> = [
      ...Object.keys(headerKeys).map((key) => ({
        key,
        title: headerKeys[key],
        dataIndex: key,
        render: (text: string, record: PartialPOType) => {

          if (key === "actions") {
            return (
              <Row
                align="middle"
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                }}
              >
                <Col>
                  <StyledActionLink to={`/po/${record.id}`}>
                    <GrView />
                  </StyledActionLink>
                </Col>
              </Row>
            );
          }

          if (key === "orderDate" || key === "expectedDeliveryDate") {
            return <div>{getDateFromISODate(record[key] || "")}</div>;
          }

          if (key === "vendorId") {
            return (
              <NavLink to={`/vendors/edit/${record.vendorId}`}>{record.vendorCode} || {record.vendorName}</NavLink>
            );
          }
          if (key === "id") {
            return (
              <NavLink to={`/po/${record.id}`}>{record.poId}</NavLink>
            );
          }

          return <>{text}</>;
        },
      })),
    ];

    setColumns(tableHeaders);
    setRows(data);
  }, [pos]);

  const Paginator = () => {
    if (pos) {
      const { pageNo, pageSize, total } = pos;
      return (
        <StyledPagination
          current={pageNo}
          pageSize={pageSize}
          total={total}
          onChange={(page, size) => {
            const filter = searchParams.toString();
            getPOData(page, size, filter);
          }}
        />
      );
    }

    return <></>;
  };

  return (
    <>
      <Row>
        <Col>
          <AppPageHeader title="All Purchase Orders GRN" />
        </Col>
      </Row>
      <Row>
        <AppActionStrip>
          <div className="action-strip__item">
            <AppFilterButton
              active={filterState}
              onClick={() => setFilterState(!filterState)}
              filterCount={filterCount}
            />
          </div>
        </AppActionStrip>
      </Row>
      <Row style={{ marginBottom: "1rem" }}>
        <AppFilter expanded={filterState}>
          <AppPoFilter showVendorName />
        </AppFilter>
      </Row>
      {rows && rows.length ? (
        <Row>
          <Col xs={24}>
            <Table
              columns={columns}
              dataSource={rows}
              scroll={{ x: "100vh", y: "65vh" }}
              size="small"
              pagination={false}
              loading={status === "loading"}
              bordered
            />
          </Col>
          <Col xs={24}>
            <Paginator />
          </Col>
        </Row>
      ) : (
        status !== "loading" && <>No Purchase Orders to show.</>
      )}
    </>
  );
};

export default GrnPage;
